<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between px-4">
      <h1 class="text-h5 font-weight-bold" style="color: #104B94;">Co-tutores</h1>
      <div style="display: flex; gap: 8px;">
        <v-btn color="#104B94" depressed dark @click="openAddDialog">
          <v-icon left>mdi-plus</v-icon>
          Agregar co-tutor
        </v-btn>
        <v-btn color="#104B94" depressed @click="fetchCoTutores">
          <v-icon color="white" left>mdi-refresh</v-icon>
          <span class="white--text">Actualizar</span>
        </v-btn>
      </div>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="coTutores"
      :loading="loading"
      :search="search"
      :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
      :items-per-page="20"
      class="elevation-1"
    >
      <template v-slot:top>
        <v-row class="pa-4 pb-2" no-gutters>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              label="Buscar por nombre o RUT"
              dense
              hide-details
              outlined
              clearable
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.nombre="{ item }">
        <span>{{ fullName(item) || 'Sin nombre' }}</span>
      </template>

      <template v-slot:item.relationshipType="{ item }">
        <span>{{ displayRelacion(item.relationshipType) }}</span>
      </template>

      <template v-slot:item.rut="{ item }">
        <span>{{ item.rut || '—' }}</span>
      </template>

      <template v-slot:item.titular="{ item }">
        <span v-if="item.user">
          {{ fullName(item.user) || item.user.email || '—' }}
        </span>
        <span v-else>—</span>
      </template>

      <template v-slot:item.createdAt="{ item }">
        <span>{{ formatDate(item.createdAt) }}</span>
      </template>

      <template v-slot:item.fileUrl="{ item }">
        <a v-if="item.fileUrl" :href="item.fileUrl" target="_blank" style="color: #104B94; text-decoration: none; font-size: 0.85rem;">Ver</a>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn icon small @click="openDetail(item)">
          <v-icon small color="#104B94">mdi-pencil-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Edit dialog -->
    <v-dialog v-model="detailDialog" max-width="540" scrollable>
      <v-card v-if="selectedCoTutor">
        <v-card-title style="color: #104B94; font-weight: 600;">
          Editar Co-tutor
          <v-spacer />
          <v-btn icon small color="red darken-1" class="mr-1" @click="confirmDeleteDialog = true">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3 pb-2">
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="editFirstName" label="Nombres" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="editLastName" label="Apellidos" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="editRut" label="RUT / DNI" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="editRelationshipType"
                :items="relationshipOptions"
                item-text="label"
                item-value="value"
                label="Tipo de relación"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>

          <div class="d-flex align-center mt-3" style="gap: 12px;">
            <a
              v-if="selectedCoTutor.fileUrl && !editFile"
              :href="selectedCoTutor.fileUrl"
              target="_blank"
              class="caption d-flex align-center"
              style="color: #104B94; gap: 4px; white-space: nowrap; text-decoration: none;"
            >
              <v-icon x-small color="#104B94">mdi-file-outline</v-icon>
              Ver archivo
            </a>
            <v-file-input
              v-model="editFile"
              :placeholder="selectedCoTutor.fileUrl ? 'Reemplazar archivo' : 'Adjuntar archivo'"
              outlined
              dense
              hide-details
              accept="image/*,.pdf"
              prepend-icon="mdi-paperclip"
              clearable
              style="flex: 1;"
            />
          </div>

          <v-row dense class="mt-3">
            <v-col cols="6" v-if="selectedCoTutor.user">
              <div class="caption grey--text text--darken-1">Titular de la cuenta</div>
              <div class="body-2 font-weight-medium">{{ fullName(selectedCoTutor.user) || selectedCoTutor.user.email || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text text--darken-1">Fecha de registro</div>
              <div class="body-2 font-weight-medium">{{ formatDate(selectedCoTutor.createdAt) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="detailDialog = false" :disabled="savingData">Cancelar</v-btn>
          <v-btn
            color="#104B94" dark depressed
            :loading="savingData"
            :disabled="savingData || !editFirstName.trim() || !editLastName.trim()"
            @click="saveCoTutorData"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar eliminación de co-tutor -->
    <v-dialog v-model="confirmDeleteDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold" style="color: #104B94;">
          Eliminar co-tutor
        </v-card-title>
        <v-card-text>
          ¿Estás segura de que quieres eliminar a
          <strong>{{ selectedCoTutor ? fullName(selectedCoTutor) : '' }}</strong>?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDeleteDialog = false" :disabled="deletingCoTutor">Cancelar</v-btn>
          <v-btn color="red darken-1" dark depressed :loading="deletingCoTutor" @click="deleteCoTutor">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Buscar titular para agregar co-tutor -->
    <v-dialog v-model="addDialog" max-width="480" @keydown.esc="addDialog = false">
      <v-card>
        <v-card-title style="color: #104B94; font-weight: 600;">
          Buscar titular
          <v-spacer />
          <v-btn icon @click="addDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="userSearchQuery"
            label="Nombre, email o RUT del titular"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-magnify"
            :loading="userSearchLoading"
            @input="onUserSearch"
          />
          <v-list v-if="userSearchResults.length" dense>
            <v-list-item
              v-for="u in userSearchResults"
              :key="u.pgId"
              @click="selectUser(u)"
              style="cursor: pointer; border-radius: 8px;"
              class="mb-1"
            >
              <v-list-item-content>
                <v-list-item-title class="body-2 font-weight-medium">{{ u.firstName }} {{ u.lastName }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ u.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else-if="userSearchQuery && !userSearchLoading" class="caption grey--text text-center mt-2">
            Sin resultados
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal para agregar co-tutor -->
    <add-co-tutor-modal
      v-model="showAddCoTutorModal"
      :userInfo="selectedUserForAdd"
      :token="adminToken"
      @co-tutor-saved="onCoTutorSaved"
    />
  </div>
</template>

<script>
import Api360Service from '../services/360.service';
import UsersService from '../services/users.service';
import AddCoTutorModal from '../components/AddCoTutorModal.vue';
import { RELATIONSHIP_TYPE_OPTIONS, displayRelationshipType } from '../utils/relationship-type.utils';

export default {
  name: 'CoTutores',
  components: { AddCoTutorModal },
  data() {
    return {
      loading: false,
      coTutores: [],
      search: '',
      detailDialog: false,
      selectedCoTutor: null,
      addDialog: false,
      userSearchQuery: '',
      userSearchResults: [],
      userSearchLoading: false,
      userSearchTimeout: null,
      selectedUserForAdd: null,
      showAddCoTutorModal: false,
      adminToken: JSON.parse(localStorage.getItem('accessToken')),
      editFirstName: '',
      editLastName: '',
      editRut: '',
      editRelationshipType: null,
      editFile: null,
      savingData: false,
      confirmDeleteDialog: false,
      deletingCoTutor: false,
      relationshipOptions: RELATIONSHIP_TYPE_OPTIONS,
      headers: [
        { text: 'Nombre', value: 'nombre', sortable: false },
        { text: 'RUT / DNI', value: 'rut', sortable: false },
        { text: 'Relación', value: 'relationshipType', sortable: true },
        { text: 'Titular', value: 'titular', sortable: false },
        { text: 'Registrado', value: 'createdAt', sortable: true },
        { text: 'Archivo', value: 'fileUrl', sortable: false, align: 'center' },
        { text: 'Editar', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  mounted() {
    this.fetchCoTutores();
  },
  methods: {
    async fetchCoTutores() {
      this.loading = true;
      try {
        const token = JSON.parse(localStorage.getItem('accessToken'));
        const data = await Api360Service.getAllCoTutors(token);
        this.coTutores = data || [];
      } catch (e) {
        console.error('Error cargando co-tutores', e);
      } finally {
        this.loading = false;
      }
    },
    openDetail(coTutor) {
      this.selectedCoTutor = coTutor;
      this.editFirstName = coTutor.firstName || '';
      this.editLastName = coTutor.lastName || '';
      this.editRut = coTutor.rut || '';
      this.editRelationshipType = coTutor.relationshipType || null;
      this.editFile = null;
      this.detailDialog = true;
    },
    async saveCoTutorData() {
      if (!this.selectedCoTutor || this.savingData) return;
      this.savingData = true;
      try {
        const token = JSON.parse(localStorage.getItem('accessToken'));
        let file = null;
        if (this.editFile) {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(this.editFile);
          });
          file = {
            base64,
            contentType: this.editFile.type,
            extension: this.editFile.name.split('.').pop(),
          };
        }
        const payload = {
          firstName: this.editFirstName.trim(),
          lastName: this.editLastName.trim(),
          relationshipType: this.editRelationshipType,
          ...(file && { file }),
        };
        if (this.editRut.trim()) payload.rut = this.editRut.trim();
        await Api360Service.updateCoTutor(this.selectedCoTutor.id, payload, token);
        Object.assign(this.selectedCoTutor, payload);
        const idx = this.coTutores.findIndex(c => c.id === this.selectedCoTutor.id);
        if (idx !== -1) this.$set(this.coTutores, idx, { ...this.coTutores[idx], ...payload });
        this.detailDialog = false;
      } catch (e) {
        console.error('Error guardando datos', e);
      } finally {
        this.savingData = false;
      }
    },
    openAddDialog() {
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.addDialog = true;
    },
    onUserSearch() {
      clearTimeout(this.userSearchTimeout);
      if (!this.userSearchQuery || this.userSearchQuery.length < 2) {
        this.userSearchResults = [];
        return;
      }
      this.userSearchTimeout = setTimeout(async () => {
        this.userSearchLoading = true;
        try {
          const res = await UsersService.getUsers(1, 10, this.userSearchQuery);
          this.userSearchResults = res?.data?.users || res?.data || [];
        } catch (e) {
          this.userSearchResults = [];
        } finally {
          this.userSearchLoading = false;
        }
      }, 350);
    },
    selectUser(user) {
      this.selectedUserForAdd = { userId: user.pgId, ...user };
      this.addDialog = false;
      this.userSearchQuery = '';
      this.userSearchResults = [];
      this.showAddCoTutorModal = true;
    },
    onCoTutorSaved() {
      this.showAddCoTutorModal = false;
      this.fetchCoTutores();
    },
    async deleteCoTutor() {
      if (!this.selectedCoTutor || this.deletingCoTutor) return;
      this.deletingCoTutor = true;
      try {
        const token = JSON.parse(localStorage.getItem('accessToken'));
        await Api360Service.deleteCoTutor(this.selectedCoTutor.id, token);
        this.coTutores = this.coTutores.filter(c => c.id !== this.selectedCoTutor.id);
        this.confirmDeleteDialog = false;
        this.detailDialog = false;
        this.selectedCoTutor = null;
      } catch (e) {
        console.error('Error eliminando co-tutor', e);
      } finally {
        this.deletingCoTutor = false;
      }
    },
    displayRelacion(type) {
      return displayRelationshipType(type);
    },
    fullName(item) {
      if (!item) return '';
      const first = item.firstName || '';
      const last = item.lastName || '';
      return [first, last].filter(Boolean).join(' ');
    },
    formatDate(iso) {
      if (!iso) return '—';
      try {
        const d = new Date(iso);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      } catch {
        return iso;
      }
    },
  },
};
</script>

<style scoped>
.custom-font {
  font-family: inherit;
}
</style>
